import { Link } from "react-router-dom";
import { ArrowUpRight, Clock } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/components/Layout";
import { BLOG_FR, BLOG_EN } from "@/data/blog";
import { Reveal, RevealLines, Stagger, StaggerItem } from "@/components/motion-primitives";
import { SectionLabel } from "@/components/ui-blocks";

export default function Blog() {
  const { lang, t } = useLang();
  const b = lang === "en" ? BLOG_EN : BLOG_FR;
  useSeo(b.seoTitle, b.seoDesc, lang);

  return (
    <div className="min-h-screen">
      {/* HERO */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pt-32 lg:pt-32 pb-8 lg:pb-12">
        <SectionLabel>{b.label}</SectionLabel>
        <RevealLines as="h1" delay={0.1} className="display-hero text-ink mt-4" lines={b.heroLines} />
        <Reveal delay={0.4}><p className="mt-4 max-w-2xl text-muted-foreground leading-relaxed">{b.sub}</p></Reveal>
      </section>

      {/* ARTICLES */}
      <section className="max-w-8xl mx-auto px-5 sm:px-6 lg:px-12 pb-16 lg:pb-24">
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" gap={0.08}>
          {b.articles.map((a, i) => (
            <StaggerItem key={a.id}>
              <Link
                to={`/blog/${a.slug}`}
                data-testid={`blog-card-${i}`}
                aria-label={a.title}
                className="group flex flex-col h-full border border-border bg-card hover:border-ink transition-colors duration-400"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={a.cover}
                    alt={a.title}
                    loading="lazy"
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute top-3 left-3 inline-flex items-center bg-night text-white text-[11px] font-bold uppercase tracking-wide px-2.5 py-1.5">
                    {a.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-3 text-muted-foreground text-xs font-mono-alt uppercase tracking-wider">
                    <Clock size={13} /> {a.readMin} {t.blog.minRead}
                  </div>
                  <h2 className="mt-3 font-display text-xl font-bold text-ink leading-snug">{a.title}</h2>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{a.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-ink group-hover:text-accentbrand transition-colors">
                    {t.blog.readMore} <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>
    </div>
  );
}
